import type { MutableState } from 'src/replicant/State';
import { assertNever } from 'src/replicant/utils';
import * as Care from '.';
import * as Fate from '../fate';
import { PetAttribute, PetStatusEffect } from '../pet';
import * as Pet from '../pet';
import {
  CareActionReceiveEvent,
  getCareActionReceiveEvent,
} from './care.analytics';
import { CareActionType, CareItem } from './care.ruleset';

function incrementCareActionCount(state: MutableState) {
  state.care.careActionCount += 1;
}

/**
 * Perform care action like: toilet and bath
 */
export function performCareAction(
  state: MutableState,
  type: CareActionType,
  now: number,
):
  | { consumed: boolean; bPointsConsumed: number; events?: undefined }
  | {
      consumed: boolean;
      bPointsConsumed: number;
      events: CareActionReceiveEvent[];
    } {
  const events = [];
  if (!Care.getters.canPerformCareAction(state, type, now)) {
    return { consumed: false, bPointsConsumed: 0 };
  }

  const friendId = state.visit.visitSession?.friendId;
  const unsyncedChanges = state.visit.visitSession?.unsyncedChanges;

  let attribute: PetAttribute = 'urine';

  const { setAttribute } = Pet.modifiers;
  switch (type) {
    case 'toilet':
      attribute = 'urine';
      break;

    case 'bath':
      attribute = 'dirtiness';
      break;

    default:
      assertNever(type);
  }

  setAttribute(state, attribute, 0, now);

  // Current player CareActionReceive analytics event
  events.push(
    getCareActionReceiveEvent(state.id, {
      action_type: type,
      item_id: undefined,
      result: undefined,
      byFriend: false,
      friendID: undefined,
      bPointsConsumed: 0,
    }),
  );

  if (friendId) {
    const current = unsyncedChanges?.attributes[attribute] ?? 0;

    if (unsyncedChanges) {
      unsyncedChanges.attributes[attribute] = current - 100;
    }

    if (state.visit.visitSession) {
      state.visit.visitSession.careActions += 1;
    }

    // Friend CareActionReceive analytics event
    events.push(
      getCareActionReceiveEvent(friendId, {
        action_type: type,
        item_id: undefined,
        result: undefined,
        byFriend: true,
        friendID: state.id,
        bPointsConsumed: 0,
      }),
    );
  }

  Fate.modifiers.update(state, now);
  incrementCareActionCount(state);

  return { consumed: true, bPointsConsumed: 0, events };
}

/**
 * Purchase care items like: food, snacks, first_aid etc...
 */
export function purchaseCareItem(
  state: MutableState,
  careItemId: CareItem['id'],
  now: number,
  random: () => number,
  fromInventory: boolean,
) {
  const events: CareActionReceiveEvent[] = [];
  const { ruleset } = Care;
  const item = ruleset.careItems[careItemId];

  const { hasTriedToCurePet, getResultOfCureAction, getCareItemAmount } =
    Care.getters;
  const { getVisiblePetState, hasPermanentStatusEffect } = Fate.getters;

  const previousPetState = getVisiblePetState(state, now);
  const price = fromInventory ? 0 : item.price;

  const { getAttributeValue, getResolutionAttempts } = Pet.getters;
  const { setAttribute, incrementResolutionAttempts } = Pet.modifiers;
  const { resolveStatusEffect } = Fate.modifiers;
  const { petAttributes, decayMultipliers, petStatusEffects } = Pet.ruleset;
  const friendId = state.visit.visitSession?.friendId;
  const unsyncedChanges = state.visit.visitSession?.unsyncedChanges;

  // Keep track of attributes that would be changed by this care item and if they have.
  const attributesChanged = {} as Record<string, boolean>;
  let refund = { energy: 0 };

  petAttributes.forEach((attribute) => {
    const amount = getCareItemAmount(item, attribute);

    if (amount) {
      const isMeterAttribute = attribute === 'love' || attribute === 'stomach';
      const max = Pet.getters.getMaxPetAttributeValue();
      const currentValue = getAttributeValue(state, attribute, now);
      const multiplier = -decayMultipliers[attribute];
      const addedValue = amount * multiplier;
      const endValue = currentValue + addedValue;

      if (isMeterAttribute) {
        // 99.999% is not 100%, but it's what we display, so treat it that way.
        attributesChanged[attribute] = Math.ceil(currentValue) < max;
      }

      setAttribute(state, attribute, endValue, now);
      // Should also update attribute of visited pet.
      if (friendId) {
        const current = unsyncedChanges?.attributes[attribute] ?? 0;
        const value = Care.getters.getCareItemAmount(item, attribute) ?? 0;

        if (unsyncedChanges) {
          unsyncedChanges.attributes[attribute] = current + value * multiplier;
        }

        if (isMeterAttribute && attributesChanged) {
          // 99.999% is not 100%, but it's what we display, so treat it that way.
          attributesChanged[attribute + '_friend'] = Math.ceil(current) < max;
        }
      }
    }
  });

  // If none of the targeted attributes were changed, the item was not technically consumed.
  const gainedAttributes = Object.values(attributesChanged).every(
    (changed) => changed,
  );

  if (!gainedAttributes) {
    refund.energy = 1;
  }

  let resolvedStatusEffect = false;
  let targetStatusEffects: PetStatusEffect[] = [];
  petStatusEffects
    // We don't want care items to persist asleep status effect.
    .filter((statusEffect) => statusEffect !== 'asleep')
    .forEach((statusEffect) => {
      let hasEffect = hasPermanentStatusEffect(state, statusEffect, now);

      const resolutionAttempts = getResolutionAttempts(state, statusEffect);
      let cureChance =
        ruleset.getCareItemCureChance(item, statusEffect, resolutionAttempts) ??
        0;

      // Check if status effect should be resolved.
      if (cureChance > 0) {
        const shouldResolve = random() < cureChance;
        targetStatusEffects.push(statusEffect);

        // Increase resolution attempts counter no matter if no local pet has no effect
        // we will need that for friend pet status effects resolution
        incrementResolutionAttempts(state, statusEffect);

        // Increment resolution attempts.
        if (hasEffect) {
          hasEffect = !shouldResolve;

          resolvedStatusEffect = resolvedStatusEffect || !hasEffect;
        } else if (!gainedAttributes) {
          // The effect isn't there to be cured, refund the cost
          refund.energy = 1;
        }

        // Even if local pet doesn't have the effect, resolve it for friend's pet.
        if (friendId && shouldResolve && unsyncedChanges) {
          unsyncedChanges.statusEffects[statusEffect] = false;
        }
      }

      resolveStatusEffect(state, statusEffect, hasEffect, now);
    });

  // Update current player pet fate
  Fate.modifiers.update(state, now);

  // Increase analytics visit session stats
  incrementCareActionCount(state);

  // Care action result for analytics
  // Contains specific logic should be used only for analytics
  const careActionResult = getResultOfCureAction(
    hasTriedToCurePet(targetStatusEffects, previousPetState),
    resolvedStatusEffect ?? false,
  );

  // Current player CareActionReceive analytics event
  events.push(
    getCareActionReceiveEvent(state.id, {
      action_type: item.type,
      item_id: item.id,
      result: careActionResult,
      byFriend: false,
      friendID: undefined,
      bPointsConsumed: price,
    }),
  );

  // Increase analytics visit session stats
  if (friendId) {
    if (state.visit.visitSession) {
      state.visit.visitSession.careActions += 1;
      state.visit.visitSession.paidCareActions += 1;
      state.visit.visitSession.coinsSpent += price;
    }

    // Friend CareActionReceive analytics event
    events.push(
      getCareActionReceiveEvent(friendId, {
        action_type: item.type,
        item_id: item.id,
        result: careActionResult,
        byFriend: true,
        friendID: state.id,
        bPointsConsumed: price,
      }),
    );
  }

  // Shortcut and reset after the fact
  refund.energy = 0;

  // Return result
  return {
    consumed: true,
    resolvedStatusEffect,
    targetStatusEffects,
    bPointsConsumed: price,
    events,
    refund,
  } as const;
}
